import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';

interface IntroOverlayProps {
  videoSrc: string;
  setIntroFinished: (finished: boolean) => void;
}

const Intro = ({ videoSrc, setIntroFinished }: IntroOverlayProps) => {
  const riyadhRef = useRef<SVGSVGElement>(null);
  const seasonRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const yearRef = useRef<HTMLDivElement>(null);
  const logoContRef = useRef<HTMLDivElement>(null);
  const contRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLDivElement>(null);
  const [logoAnimation, setLogoAnimationFinished] = useState(false);
  const [animationFinished, setAnimationFinished] = useState(false);
  useEffect(() => {
    if (!logoAnimation) return;
    const tl = gsap.timeline();
    // set scale to 2

    gsap.set(riyadhRef.current, {
      scale: 3,
      opacity: 0,
      x: -600,
      transformOrigin: 'center center',
    });
    gsap.set(seasonRef.current, {
      scale: 2,
      opacity: 0,
      x: 600,
      transformOrigin: 'center center',
    });

    tl.to(riyadhRef.current, {
      duration: 1,
      scale: 1,
      opacity: 1,
      ease: 'elastic.out(1, 1)',
      x: 0,
    })
      .to(
        seasonRef.current,
        {
          duration: 1,
          scale: 1,
          opacity: 1,
          ease: 'elastic.out(1, 1)',
          x: 0,
        },
        '<'
      )
      .to(
        containerRef.current,
        {
          duration: 0.5,
          scale: 1,
          opacity: 1,

          x: 0,
          marginBottom: '0px',
        },
        '<'
      )
      .to(
        yearRef.current,
        {
          duration: 0.5,
          scale: 1,
          opacity: 1,
          ease: 'power2.out',
          x: 0,
          marginBottom: '0px',
        },
        '<'
      )
      .to(logoContRef.current, {
        duration: 0.5,
        width: '300px',
        height: '300px',
        ease: 'elastic.out(1, 1)',
      })
      .to(
        videoRef.current,
        {
          duration: 0.5,
          opacity: 1,
          scale: 1.1,
          ease: 'elastic.out(1, 1)',
        },
        '<0.2'
      )
      .to(logoContRef.current, {
        duration: 2,
        opacity: 1,
        scale: 1,
        width: '100%',
        height: '100%',
        ease: 'elastic.out(1, 1)',
      })
      .to(
        videoRef.current,
        {
          duration: 1,
          opacity: 1,
          scale: 1,
          width: '100vw',
          height: '100vh',
          ease: 'elastic.out(1, 1)',
          borderRadius: 0,
        },
        '<'
      )
      .to(
        [containerRef.current, yearRef.current],
        {
          duration: 0.2,
          opacity: 0,

          ease: 'power2.inOut',
        },
        '<'
      )

      .to(contRef.current, {
        duration: 0.5,
        opacity: 0,

        ease: 'power2.inOut',
        onComplete: () => {
          setAnimationFinished(true);
          setIntroFinished(true);
        },
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps -- we want this to run only when logo animation changes
  }, [logoAnimation]);
  if (animationFinished) return null;
  return (
    <>
      <style>{'body{overflow:hidden}'}</style>
      <div
        ref={contRef}
        className='fixed inset-0 left-0 top-0 z-[9999] !mt-0 flex flex-col items-center justify-center overflow-hidden bg-black uppercase text-white'
      >
        {/* Background video */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className='no-controls absolute left-0 top-0 z-10 h-full w-full object-cover'
          src='/images/faq/faq_bg_video.mp4'
        />
        <div
          ref={containerRef}
          className='relative z-20 mb-[-64px] flex items-center justify-center gap-4 rtl:flex-row-reverse'
        >
          {/* Riyadh */}
          <svg
            ref={riyadhRef}
            width='371'
            height='55'
            viewBox='0 0 371 55'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            className='hidden w-[25vw] opacity-0 md:block'
          >
            <path d='M85.5897 0H72.9023V54.027H85.5897V0Z' fill='white' />
            <path
              d='M133.683 54.027H146.37V32.1845L166.178 0H152.078L140.041 21.5317L127.975 0H113.875L133.683 32.1845V54.027Z'
              fill='white'
            />
            <path
              d='M299.833 27.0135C299.833 8.98565 287.23 0 270.248 0H251.909V54.027H270.248C287.315 54.027 299.833 45.0978 299.833 27.0135ZM264.512 10.4833H270.926C281.183 10.4833 286.976 16.8128 286.976 27.0135C286.976 37.2142 280.957 43.2894 270.926 43.2894H264.512V10.4833Z'
              fill='white'
            />
            <path
              d='M357.675 0V20.9101H336.002V0H323.314V54.027H336.002V31.3933H357.675V54.027H370.362V0H357.675Z'
              fill='white'
            />
            <path
              d='M40.9724 16.2195C40.9724 4.37986 31.1107 0 20.7688 0H0V54.027H12.6873V33.3431H18.0278L29.3871 54.027H44.1089L30.4043 31.7041C34.3885 30.4608 40.9724 26.4484 40.9724 16.1912M18.9603 23.6509H12.6025V9.77691H18.9603C23.6509 9.77691 28.2003 11.0485 28.2003 16.6716C28.2003 22.2947 23.6509 23.6509 18.9603 23.6509Z'
              fill='white'
            />
            <path
              d='M196.328 0L174.655 54.027H187.795L191.779 43.2329H213.395L217.38 54.027H231.169L209.496 0H196.328ZM195.452 33.3713L202.629 13.9589L209.807 33.3713H195.481H195.452Z'
              fill='white'
            />
          </svg>
          <div ref={logoContRef} className='size-[140px]'>
            <AnimatedLogo onFinish={() => setLogoAnimationFinished(true)} />
          </div>
          {/* Season */}
          <svg
            ref={seasonRef}
            width='375'
            height='56'
            viewBox='0 0 375 56'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            className='hidden w-[25vw] opacity-0 md:block'
          >
            <path
              d='M276.266 0C260.838 0 248.349 12.4895 248.349 27.9177C248.349 43.3459 260.838 55.8355 276.266 55.8355C291.695 55.8355 304.184 43.3459 304.184 27.9177C304.184 12.4895 291.695 0 276.266 0ZM276.266 43.2047C267.846 43.2047 260.979 36.3383 260.979 27.9177C260.979 19.4972 267.846 12.6308 276.266 12.6308C284.687 12.6308 291.553 19.4972 291.553 27.9177C291.553 36.3383 284.687 43.2047 276.266 43.2047Z'
              fill='white'
            />
            <path
              d='M28.4829 23.6798C26.7592 22.9734 22.775 21.5605 20.4014 20.628C17.9713 19.6955 14.9196 18.424 14.9196 15.1462C14.9196 11.8684 17.8866 10.286 21.9556 10.286C26.0246 10.286 30.8847 12.6313 34.0212 15.7678L39.4183 6.92344C35.7449 3.25006 28.5394 0.254883 22.2664 0.254883C15.9934 0.254883 11.0767 1.97854 8.02492 4.57817C4.88841 7.14954 2.37356 11.2185 2.37356 16.3895C2.37356 21.5605 4.26677 24.6122 6.61208 26.8163C8.95739 29.0768 11.6984 30.0941 13.0264 30.7157C14.8349 31.5069 18.9039 32.9197 21.2492 33.7674C23.5945 34.6434 27.5787 36.028 27.5787 39.7296C27.5787 43.4313 24.4422 45.2962 20.3732 45.2962C16.3042 45.2962 10.2007 42.5553 6.27301 38.4863L0 47.7263C4.3798 51.7952 11.3593 55.8642 19.1864 55.8642C29.6697 55.8642 40.1812 50.1564 40.1812 38.4863C40.1812 30.6592 35.321 26.1946 28.4263 23.6798'
              fill='white'
            />
            <path
              d='M75.7286 32.1563H96.8646V22.0403H75.7286V10.9919H98.1362V0.904297H63.126V54.9313H98.8426V44.674H75.7286V32.1563Z'
              fill='white'
            />
            <path
              d='M216.56 23.6798C214.836 22.9734 210.852 21.5605 208.479 20.628C206.049 19.6955 202.997 18.424 202.997 15.1462C202.997 11.8684 205.964 10.286 210.061 10.286C214.158 10.286 218.99 12.6313 222.127 15.7678L227.524 6.92344C223.85 3.25006 216.645 0.254883 210.372 0.254883C204.099 0.254883 199.182 1.97854 196.13 4.57817C192.994 7.14954 190.479 11.2185 190.479 16.3895C190.479 21.5605 192.372 24.6122 194.718 26.8163C197.063 29.0768 199.804 30.0941 201.132 30.7157C202.94 31.5069 207.009 32.9197 209.355 33.7674C211.7 34.6434 215.684 36.028 215.684 39.7296C215.684 43.4313 212.548 45.2962 208.479 45.2962C204.41 45.2962 198.306 42.5553 194.378 38.4863L188.105 47.7263C192.485 51.7952 199.465 55.8642 207.292 55.8642C217.775 55.8642 228.287 50.1564 228.287 38.4863C228.287 30.6592 223.426 26.1946 216.532 23.6798'
              fill='white'
            />
            <path
              d='M361.743 25.4029C361.743 31.1107 361.969 37.2142 361.969 37.2142L338.799 0.875977H325.235V54.903H337.612V30.4608C337.612 24.753 337.386 18.2539 337.386 18.2539L361.122 54.903H374.12V0.875977H361.743V25.3747V25.4029Z'
              fill='white'
            />
            <path
              d='M137.187 0.904297L115.514 54.9313H128.653L132.637 44.1372H154.254L158.238 54.9313H172.027L150.354 0.904297H137.187ZM136.282 34.2473L143.46 14.8349L150.637 34.2473H136.311H136.282Z'
              fill='white'
            />
          </svg>
        </div>
        <div
          ref={yearRef}
          className='font-gellix relative z-20 h-0 text-[64px] text-white opacity-0'
        >
          <span className='font-[100]'>20</span>
          <strong className='font-[700] text-[#3885FF]'>25</strong>
        </div>
        {/* Fading out */}
        <div className='absolute inset-0 bg-black' />
        <div
          ref={videoRef}
          className='absolute z-10 size-[140px] overflow-hidden rounded-full opacity-0'
        >
          <video
            id='intro-video'
            autoPlay
            loop
            muted
            playsInline
            className='no-controls z-10 size-full h-full w-full object-cover'
            src={videoSrc}
          />
        </div>
      </div>
    </>
  );
};

const AnimatedLogo = ({ onFinish }: { onFinish: () => void }) => {
  useEffect(() => {
    const paths = document.querySelectorAll('#animated-logo path');
    const firstPath = paths[0];
    const restPaths = Array.from(paths).slice(1);
    const tl = gsap.timeline();

    // First set all paths to have white border and no fill
    gsap.set(paths, {
      stroke: '#ffffff33',
      strokeWidth: 1,
      fill: 'none',
    });

    // Create animation timeline
    tl.to(firstPath, {
      duration: 1,
      opacity: 1,
      fill: (_, target) => target.getAttribute('data-fill'),
    }).to(restPaths, {
      duration: 1,
      opacity: 1,

      stroke: 'none',
      fill: (_, target) => target.getAttribute('data-fill'),
      stagger: 0.2,
      onComplete: onFinish,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps -- we want this to run only once on mount
  }, []);

  return (
    <svg
      id='animated-logo'
      width='140'
      height='140'
      viewBox='0 0 140 140'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className='relative z-20 h-full w-full'
    >
      <g clipPath='url(#clip0_3629_34782)'>
        <path
          data-fill='#3885FF'
          stroke='#ffffff33'
          strokeWidth='2'
          fillRule='evenodd'
          clipRule='evenodd'
          d='M140 73.9919C137.903 110.8 107.362 140 70 140C32.6377 140 2.10316 110.8 0 73.9919H35.5273C37.5351 91.2342 52.2036 104.622 70 104.622C87.7964 104.622 102.465 91.2402 104.473 73.9919H140Z'
        />
        <path
          data-fill='#FF9B27'
          stroke='#ffffff33'
          strokeWidth='2'
          fillRule='evenodd'
          clipRule='evenodd'
          d='M38.1189 56.2193C36.8141 59.246 35.9264 62.493 35.5332 65.8891H0.00589929C0.565947 56.0704 3.15169 46.7938 7.34609 38.4646L38.1189 56.2193Z'
        />
        <path
          data-fill='#987BFF'
          stroke='#ffffff33'
          strokeWidth='2'
          fillRule='evenodd'
          clipRule='evenodd'
          d='M49.2484 42.1347C46.5614 44.1426 44.1722 46.5258 42.1644 49.2128L11.3915 31.4581C16.6465 23.4804 23.4921 16.6407 31.4818 11.3858L49.2484 42.1347Z'
        />
        <path
          data-fill='#32C200'
          stroke='#ffffff33'
          strokeWidth='2'
          fillRule='evenodd'
          clipRule='evenodd'
          d='M65.9245 0V35.4975C62.5225 35.8907 59.2754 36.7785 56.2488 38.0832L38.4822 7.33424C46.8174 3.13984 56.0998 0.560048 65.9245 0Z'
        />
        <path
          data-fill='#ACACAC'
          stroke='#ffffff33'
          strokeWidth='2'
          fillRule='evenodd'
          clipRule='evenodd'
          d='M101.493 7.33424L83.7269 38.0832C80.7002 36.7785 77.4472 35.8907 74.0512 35.4975V0C83.8758 0.560048 93.1583 3.13984 101.493 7.33424Z'
        />
        <path
          data-fill='#FA5F00'
          stroke='#ffffff33'
          strokeWidth='2'
          fillRule='evenodd'
          clipRule='evenodd'
          d='M128.597 31.4581L97.8238 49.2128C95.8159 46.5258 93.4327 44.1426 90.7397 42.1347L108.506 11.3858C116.49 16.6407 123.336 23.4804 128.597 31.4581Z'
        />
        <path
          data-fill='#00D2B0'
          stroke='#ffffff33'
          strokeWidth='2'
          fillRule='evenodd'
          clipRule='evenodd'
          d='M139.982 65.8891H104.455C104.062 62.493 103.174 59.246 101.869 56.2193L132.642 38.4646C136.836 46.7938 139.422 56.0704 139.982 65.8891Z'
        />
      </g>
      <defs>
        <clipPath id='clip0_3629_34782'>
          <rect width='140' height='140' fill='white' />
        </clipPath>
      </defs>
    </svg>
  );
};

export default Intro;
